import { HttpShopEstilos } from "../../http/httpShopEstilos";
import { hidratarLG } from "../../styles/liquidGlass";
import type { ShopEstilosConfig } from "../../types/ShopEstilosConfig";

let _logoFolder = "cream";
let _logoExt: { favicon: "svg" | "png"; pill: "svg" | "png"; stacked: "svg" | "png" } = {
    favicon: "svg",
    pill: "svg",
    stacked: "svg",
};

export function getLogoAssets() {
    return {
        favicon: `/images/logo/${_logoFolder}/favicon.${_logoExt.favicon}`,
        pill: `/images/logo/${_logoFolder}/pill.${_logoExt.pill}`,
        stacked: `/images/logo/${_logoFolder}/stacked.${_logoExt.stacked}`,
    };
}

async function probeLogoExt(folder: string, name: string): Promise<"svg" | "png"> {
    // Probamos cargando la imagen como tal: si decodea, existe. Esto evita el
    // falso positivo del fallback SPA de Vite dev que devuelve 200 con HTML
    // cuando el asset no existe.
    const exists = await new Promise<boolean>((resolve) => {
        const img = new Image();
        img.onload = () => resolve(img.naturalWidth > 0);
        img.onerror = () => resolve(false);
        img.src = `/images/logo/${folder}/${name}.svg?probe=${Date.now()}`;
    });
    return exists ? "svg" : "png";
}

async function aplicarAssets(config: ShopEstilosConfig): Promise<void> {
    _logoFolder = config.assets?.logoFolder ?? "cream";

    const [favExt, pillExt, stackedExt] = await Promise.all([
        probeLogoExt(_logoFolder, "favicon"),
        probeLogoExt(_logoFolder, "pill"),
        probeLogoExt(_logoFolder, "stacked"),
    ]);
    _logoExt = { favicon: favExt, pill: pillExt, stacked: stackedExt };

    const link = document.querySelector<HTMLLinkElement>(
        'link[rel="icon"][type="image/svg+xml"], link[rel="icon"][type="image/png"]',
    );
    if (link) {
        link.href = `/images/logo/${_logoFolder}/favicon.${favExt}`;
        link.type = favExt === "svg" ? "image/svg+xml" : "image/png";
    }
}

const STORAGE_KEY = "voda_argentina_estilos_v1";

function aplicarCSSVars(config: ShopEstilosConfig): void {
    const root = document.documentElement;
    const { colores: c, radios: r, blur: b, sombras: s, gradiente: g, tipografia: t } = config;

    // Colores base
    root.style.setProperty("--cream-50", c.fondoClaro);
    root.style.setProperty("--cream-100", c.fondo);
    root.style.setProperty("--cream-200", c.fondoMedio);
    root.style.setProperty("--cream-300", c.fondoCalido);
    root.style.setProperty("--cream-400", c.fondoIntenso);
    root.style.setProperty("--ink-900", c.texto);
    root.style.setProperty("--ink-800", c.textoInk800);
    root.style.setProperty("--ink-700", c.textoInk700);
    root.style.setProperty("--ink-500", c.textoSecundario);
    root.style.setProperty("--ink-400", c.textoMuted);
    root.style.setProperty("--ink-300", c.textoDeshabilitado);
    root.style.setProperty("--gold", c.gold);
    root.style.setProperty("--gold-soft", c.goldSoft);

    // Superficies glass
    root.style.setProperty("--glass-bg", c.superficieGlass);
    root.style.setProperty("--glass-bg-strong", c.superficieGlassStrong);
    root.style.setProperty("--glass-bg-dark", c.superficieGlassDark);
    root.style.setProperty("--appbar-bg", c.superficieAppbar);
    root.style.setProperty("--menu-bg", c.superficieMenu);
    root.style.setProperty("--surface-hover", c.superficieHover);

    // Botones, bordes y divisores
    root.style.setProperty("--btn-dark-bg", c.botonOscuroBg);
    root.style.setProperty("--btn-dark-text", c.botonOscuroTexto);
    root.style.setProperty("--border-color", c.borde);
    root.style.setProperty("--border-color-strong", c.bordeStrong);
    root.style.setProperty("--border-dark-color", c.bordeDark);
    root.style.setProperty("--divider", c.divider);

    // Bordes compuestos (calculados)
    root.style.setProperty("--glass-border", `1px solid ${c.borde}`);
    root.style.setProperty("--glass-border-strong", `1px solid ${c.bordeStrong}`);
    root.style.setProperty("--glass-border-dark", `1px solid ${c.bordeDark}`);

    // Radios de borde
    root.style.setProperty("--radius-xs", r.xs);
    root.style.setProperty("--radius-sm", r.sm);
    root.style.setProperty("--radius-md", r.md);
    root.style.setProperty("--radius-lg", r.lg);
    root.style.setProperty("--radius-xl", r.xl);
    root.style.setProperty("--radius-pill", r.pill);

    // Blur / backdrop-filter
    root.style.setProperty("--blur-normal", b.normal);
    root.style.setProperty("--blur-light", b.light);
    root.style.setProperty("--blur-strong", b.strong);

    // Sombras
    root.style.setProperty("--shadow-sm", s.sm);
    root.style.setProperty("--shadow-md", s.md);
    root.style.setProperty("--shadow-lg", s.lg);
    root.style.setProperty("--shadow-xl", s.xl);
    root.style.setProperty("--glass-border-in", s.glassBorde);
    root.style.setProperty("--glass-shadow", s.glassNormal);
    root.style.setProperty("--glass-shadow-lg", s.glassLg);
    root.style.setProperty("--glass-shadow-dark", s.glassDark);

    // Gradientes de fondo de página
    root.style.setProperty("--page-gradient-1", g.pagina1);
    root.style.setProperty("--page-gradient-2", g.pagina2);

    // Tipografía
    root.style.setProperty("--font-family", t.fontFamily);
}

export async function cargarEstilos(): Promise<void> {
    const cached = localStorage.getItem(STORAGE_KEY);

    let config: ShopEstilosConfig;

    if (cached) {
        const bitModificados = await HttpShopEstilos.obtenerBitModificados();
        if (bitModificados) {
            config = await HttpShopEstilos.obtenerEstilos();
            localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
        } else {
            config = JSON.parse(cached) as ShopEstilosConfig;
        }
    } else {
        config = await HttpShopEstilos.obtenerEstilos();
        localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
    }

    aplicarCSSVars(config);
    hidratarLG(config);
    await aplicarAssets(config);
}
